import {
  calculatePointsPCA,
  DataProjectionCoordinate,
  GeocentricCoordinate,
  geometryToArray,
  SceneCoordinate
} from '../projections';
import {MeasurementController} from '.';
import {getArea, getLength} from 'ol/sphere';
import {LineString, Polygon} from 'ol/geom';
import Delaunator from 'delaunator';
import earcut from 'earcut';
import {nanoid} from '@reduxjs/toolkit';
import {isMobile, pointInPolygon, randomHexColor} from '../utilities';
import {t} from '../../../localization';
import {
  BufferGeometry,
  DoubleSide,
  MathUtils,
  Mesh,
  MeshBasicMaterial,
  Triangle,
  Vector3,
  Vector2
} from 'three';
import {spiralIntersect} from "./land-xml-calculation";
import {toast} from "../../../app";
import { PointCloudManager } from '../pointcloud';
import { MeasureType, SamplingRate, VolumeType } from '../../../types/measurements';

export type MeasurementType =  MeasurementArea
  | MeasurementLine
  | MeasurementPoint
  | MeasurementVolume
  | SnapDownMeasurement
  | SnapUpMeasurement
  | HeightMeasurement
  | StationMeasurement

export class Measurement {
  public controller: MeasurementController;
  public id = nanoid();
  public type: MeasureType;
  private _header = 'measure.measurement';
  private _title = 'measure.measurement';
  public timeCreated = new Date();

  get title() {
    return t(this._title);
  }

  get header() {
    return t(this._header);
  }

  get timestamp() {
    return Math.floor(this.timeCreated.getTime() / 1000);
  }

  get cancelMessage() {
    return isMobile
      ? t('measure.long-press-to-cancel')
      : t('measure.right-click-to-cancel');
  }

  get startTooltip() {
    console.warn("Not Implemented");
    return [] as string[];
  }

  get startTooltipMulti() {
    return [
      t('measure.click-to-add-observation-1')
    ];
  }

  get endTooltip() {
    console.warn("Not Implemented");
    return [] as string[];
  }

  get endTooltipMulti() {
    console.warn("Not Implemented");
    return [] as string[];
  }

  get moreTooltip() {
    console.warn("Not Implemented");
    return [] as string[];
  }

  get moreTooltipMulti() {
    console.warn("Not Implemented");
    return [] as string[];
  }

  get viewer() {
    return this.controller.viewer;
  }

  get pointclouds() {
    return this.controller.pointclouds;
  }

  get valid() {
    return true;
  }

  setController(controller) {
    this.controller = controller;
  }

  setTitle(title) {
    this._title = title;
  }

  setHeader(header) {
    this._header = header;
  }

  setTimestamp(isoTime) {
    this.timeCreated = new Date(isoTime);
  }

  clear() {}
}

export class Measurement3D extends Measurement {
  public vertices:Array<GeocentricCoordinate> = [];
  public maxVertexLength = 2;
  public minVertexRequired = 2;
  public type = MeasureType.Length;

  constructor() {
    super();

    this.setTitle("measure.3d-measurement");
    this.setHeader("measure.3d-measurement");
  }

  get startTooltip() {
    return [
      t('measure.click-to-start-line')
    ];
  }

  get canFinish() {
    return (this.vertices.length + 1) >= this.minVertexRequired;
  }

  get hasPoints() {
    return this.vertices.length > 0;
  }

  get numVertices() {
    return this.vertices.length;
  }

  /** Return 3d distance in meters, based on scene view coordinates */
  get distance3D() {
    return this.getLineDistance(false);
  }

  /** Return 2d distance in meters, based on scene view coordinates */
  get distance2D() {
    return this.getLineDistance(true);
  }

  /** Get measurement height difference in meters, based on scene view coordinates */
  get rise() {
    // Currently uses the first + last point
    let lastIndex = this.vertices.length - 1;
    let firstPoint = this.vertices[0].toScene();
    let lastPoint  = this.vertices[lastIndex].toScene();
    let height = Math.abs(lastPoint.z - firstPoint.z);

    return height;
  }

  get angle() {
    let numberOfPoints = this.vertices.length;
    if (numberOfPoints !== 2) {
      return NaN;
    }

    let angle = Math.atan(this.rise / this.distance2D);
    return MathUtils.radToDeg(angle);
  }

  get grade() {
    let numberOfPoints = this.vertices.length;
    if (numberOfPoints !== 2) {
      return NaN;
    }

    return 100 * (this.rise / this.distance2D);
  }

  get coordinates() {
    return this.vertices.map(vertex => {
      return vertex.toArray();
    });
  }

  get sceneVertices() {
    return this.vertices.map(vertex => {
      return vertex.toScene().toArray();
    });
  }

  add(vertex) {
    let finished = false;
    let addedPoint = false;

    let coordinate = new SceneCoordinate(vertex).toGeocentric();

    if (this.vertices.length === 0) {
      this.vertices.push(coordinate);
      addedPoint = true;
    } else {
      // We only add a new point if the distance travelled is large enough
      let oldVertex = this.vertices[this.vertices.length - 1];
      if (oldVertex.distanceTo(coordinate) > 0.10) {
        // New point is far enough away from previous point
        this.vertices.push(coordinate);
        addedPoint = true;
      } else {
        // Clicked the same point twice, so we finish the measurement
        // if we have gathered enough points
        if (this.vertices.length >= this.minVertexRequired) {
          finished = true;
        }
      }
    }

    // For types with a fixed number of points, set finished to
    // true if we have reached the max number of points
    if (!finished) {
      finished = this.vertices.length === this.maxVertexLength;
    }

    return {
      finished: finished,
      added: addedPoint,
      valid: true
    };
  }

  getProjectedVertices(projection) {
    let vertices = this.vertices.map(vertex => {
      return vertex.toProjection(projection).toArray();
    });

    if (this instanceof MeasurementArea) {
      vertices.push(vertices[0]);
    }

    return vertices;
  }

  /** Private */
  getLineDistance(ignoreHeight) {
    let vertexA;
    let vertexB;

    let distance = 0;
    let vertices = this.vertices;

    for (var i = 1; i < vertices.length; i++) {
      let prevIndex = i - 1;
      vertexA = vertices[prevIndex].toScene();
      vertexB = vertices[i].toScene();

      if (ignoreHeight) {
        vertexA.z = 0;
        vertexB.z = 0;
      }

      distance += vertexA.distanceTo(vertexB);
    }

    return distance;
  }
}

export class StationMeasurement extends Measurement3D {
  public type = MeasureType.Station;
  public distAlong = 0.0;
  public distAcross = 0.0;
  public stationStart = 0.0;
  public sideOfLine = 'R';
  private clickedPoint;
  private enu = false;
  public units = 'm';

  constructor() {
    super();

    this.setTitle("measure.station-measure");
    this.setHeader("measure.station-info");
  }

  get activeAlignment() {
    return this.controller.activeAlignment;
  }

  get alignmentInfo() {
    return this.controller.getAlignmentByID(this.activeAlignment);
  }

  get startTooltip() {
    if (this.activeAlignment) {
      return [
        t('measure.click-to-station')
      ];
    } else {
      return [
        t('measure.choose-alignment')
      ];
    }
  }

  get distanceAcross() {
    return this.distAcross;
  }

  get distanceAlong() {
    return this.distAlong;
  }

  get staStart() {
    return this.stationStart;
  }

  get sideOfSegment() {
    return this.sideOfLine;
  }

  isBetween(segment) {
    if (segment.shapeType === 'Curve') {
      const curveCheck = this.curveIntersect(segment, true);
      if (!curveCheck) {
        return false;
      }
    }

    const x1 = segment.Start[0];
    const x2 = segment.End[0];
    const x3 = this.clickedPoint[0];

    const y1 = segment.Start[1];
    const y2 = segment.End[1];
    const y3 = this.clickedPoint[1];

    const distEndsSquared = Math.pow(x1-x2, 2) + Math.pow(y1-y2, 2);

    const u = ((x3-x1)*(x2-x1)+(y3-y1)*(y2-y1)) / distEndsSquared;
    const deltaX = x1 + u * (x2-x1);
    const deltaY = y1 + u * (y2-y1);

    const epsilon = 0.001;
    const crossProduct = (deltaY - y1) * (x2 - x1) - (deltaX - x1) * (y2 - y1);
    if (Math.abs(crossProduct) > epsilon) {
      return false;
    }

    const dotProduct = (deltaX - x1)*(x2 - x1) + (deltaY - y1)*(y2 - y1);
    return !(dotProduct < 0 || dotProduct > distEndsSquared);
  }

  getClosestSegment(segments) {
    let minDist;
    let chosenSegment;
    segments.forEach(segmentTest => {
      this.lineIntersect(segmentTest, true);
      if (!minDist) {
        minDist = this.distAcross;
      }
      if(this.distAcross<=minDist) {
        chosenSegment = segmentTest;
        minDist = this.distAcross;
      }
    });

    return chosenSegment;
  }

  lineIntersect(segment, checkOnly=false) {
    const x1 = segment.Start[0];
    const x2 = segment.End[0];
    const x3 = this.clickedPoint[0];

    const y1 = segment.Start[1];
    const y2 = segment.End[1];
    const y3 = this.clickedPoint[1];

    const distEndsSquared = Math.pow(x1-x2, 2) + Math.pow(y1-y2, 2);

    const u = ((x3-x1)*(x2-x1)+(y3-y1)*(y2-y1)) / distEndsSquared;
    const deltaX = x1 + u * (x2-x1);
    const deltaY = y1 + u * (y2-y1);

    let intersectCoordinate;
    let clickedCoordinate;
    let startCoordinate;

    if (this.enu){
      clickedCoordinate = new DataProjectionCoordinate(
        [this.clickedPoint[0], this.clickedPoint[1],
          this.clickedPoint[2]]);
      startCoordinate = new DataProjectionCoordinate([x1, y1,
        this.clickedPoint[2]]);
      intersectCoordinate = new DataProjectionCoordinate(
        [deltaX, deltaY, this.clickedPoint[2]]);
    } else {
      clickedCoordinate = new DataProjectionCoordinate(
        [this.clickedPoint[1], this.clickedPoint[0],
          this.clickedPoint[2]]);
      startCoordinate = new DataProjectionCoordinate([y1, x1,
        this.clickedPoint[2]]);
      intersectCoordinate = new DataProjectionCoordinate(
        [deltaY, deltaX, this.clickedPoint[2]]);
    }

    if (!checkOnly) {
      this.vertices.unshift(intersectCoordinate.toGeocentric());
    }

    const distAlong = new Vector2(...startCoordinate)
      .distanceTo(new Vector2(...intersectCoordinate));

    const distAcross = new Vector2(...clickedCoordinate)
      .distanceTo(new Vector2(...intersectCoordinate));

    const sideValue = (x1-x2)*(y3-y2) - (y1-y2)*(x3-x2);
    const side = sideValue > 0 ? 'L':'R';

    this.distAlong = distAlong;
    this.distAcross = distAcross;
    this.sideOfLine = side;
    // console.log(`across: ${this.distAcross} | along: ${this.distAlong} | side: ${this.sideOfLine}`);

    return true;
  }

  curveIntersect(segment, checkOnly=false) {
    const rotation = segment.rot;
    const radius = segment.radius;
    const center = segment.Center.slice(0, 2);
    const point = this.clickedPoint;

    const distance = Math.sqrt(Math.pow(center[0] - point[0], 2)
        + Math.pow(center[1] - point[1], 2));

    const distAcross = radius - distance;

    const pointVector = [(point[0] - center[0]) / distance,
      (point[1] - center[1]) / distance];

    const startVector = [(segment.Start[0] - center[0]) / radius,
      (segment.Start[1] - center[1]) / radius];

    const endVector = [(segment.End[0] - center[0]) / radius,
      (segment.End[1] - center[1]) / radius];

    let pointArc = [center[0] + (radius*pointVector[0]),
      center[1]+(radius*pointVector[1])];

    const distStartToArc = Math.sqrt(Math.pow(pointArc[0] - segment.Start[0], 2)
        + Math.pow(segment.Start[1] - pointArc[1], 2));

    const calcAngle = Math.acos(1 - (distStartToArc * distStartToArc) /
      (2 * radius * radius));

    let angle = Math.atan2(pointVector[1], pointVector[0]);
    angle = (angle < 0) ? angle + Math.PI*2 : angle;
    let angleStart = Math.atan2(startVector[1], startVector[0]);
    angleStart = (angleStart < 0) ? angleStart + Math.PI*2 : angleStart;
    let angleEnd = Math.atan2(endVector[1], endVector[0]);
    angleEnd = (angleEnd < 0) ? angleEnd + Math.PI*2 : angleEnd;
    let angleLarge = (angleStart > angleEnd) ? angleStart : angleEnd;
    let angleSmall = (angleStart < angleEnd) ? angleStart : angleEnd;
    let angleSmallQuadrant = angleSmall;
    let angleLargeQuadrant = angleLarge;
    let angleQuadrant = angle;

    // console.log(`original rotations: ${angle} | ${angleStart} | ${angleEnd}`)
    // console.log(`original size rotations: ${angle} | ${angleSmall} | ${angleLarge}`)

    if ((angleSmall > 0) && (angleSmall < Math.PI/2)) {
      // console.log('small angle check')
      if ((angleLarge < Math.PI*2) && (angleLarge > Math.PI * 1.5)) {
        // console.log('big angle check')
        angleLargeQuadrant = angleSmall + (Math.PI/2);
        angleLargeQuadrant = (angleLargeQuadrant > Math.PI*2) ? angleLargeQuadrant - Math.PI*2 : angleLargeQuadrant;
        angleSmallQuadrant = angleLarge + (Math.PI/2);
        angleSmallQuadrant = (angleSmallQuadrant > Math.PI*2) ? angleSmallQuadrant - Math.PI*2 : angleSmallQuadrant;
        angleQuadrant += (Math.PI/2);
        angleQuadrant = (angleQuadrant > Math.PI*2) ? angleQuadrant - Math.PI*2 : angleQuadrant;
      }
    }
    // console.log(`altered size rotations: ${angleQuadrant} | ${angleSmallQuadrant} | ${angleLargeQuadrant}`)
    let angleReverse = angleQuadrant - Math.PI;
    angleReverse = (angleReverse < 0) ? angleReverse + Math.PI*2 : angleReverse;

    if ((angleQuadrant <= angleSmallQuadrant) || (angleQuadrant >= angleLargeQuadrant)) {
      if ((angleReverse <= angleSmallQuadrant) || (angleReverse >= angleLargeQuadrant)) {
        // console.log(`failed rotations: ${angleReverse} | ${angleSmallQuadrant} | ${angleLargeQuadrant}`)
        return false;
      }
      // console.log(`succeeded rotations: ${angleReverse} | ${angleSmallQuadrant} | ${angleLargeQuadrant}`)
    }

    const distAlong = radius * calcAngle;

    if (rotation === "ccw"){
      if (distAcross > 0){
        this.sideOfLine = "L";
      } else {
        this.sideOfLine = "R";
      }
    } else if (rotation === "cw") {
      if (distAcross > 0) {
        this.sideOfLine = "R";
      } else {
        this.sideOfLine = "L";
      }
    } else {
      console.error(`Rotation Type ${rotation} is invalid for this geometry`);
      return false;
    }

    if (!checkOnly){
      this.distAcross = Math.abs(distAcross);
      this.distAlong = distAlong;
      let coordinate;
      if (this.enu){
        coordinate = new DataProjectionCoordinate(
          [pointArc[0], pointArc[1], this.clickedPoint[2]]).toGeocentric();
      } else {
        coordinate = new DataProjectionCoordinate(
          [pointArc[1], pointArc[0], this.clickedPoint[2]]).toGeocentric();
      }
      this.vertices.unshift(coordinate);
      // console.log(`across: ${this.distAcross} | along: ${this.distAlong} | side: ${this.sideOfLine}`);
    }

    return true;
  }

  spiralIntersection(spiral) {
    const results = spiralIntersect(spiral, this.clickedPoint);
    if (!results) return false;

    let coordinate;
    if (this.enu){
      coordinate = new DataProjectionCoordinate(
        [results.coordinates[0], results.coordinates[1],
          this.clickedPoint[2]]).toGeocentric();
    } else {
      coordinate = new DataProjectionCoordinate(
        [results.coordinates[1], results.coordinates[0],
          this.clickedPoint[2]]).toGeocentric();
    }
    this.distAlong = results.distAlong;
    this.distAcross = results.distAcross;
    this.sideOfLine = results.sideOfLine;
    this.vertices.unshift(coordinate);
  }

  getIntersect(segment) {
    this.stationStart = segment.staStart;
    if (segment.shapeType === "Line") {
      return this.lineIntersect(segment);
    } else if (segment.shapeType === "Curve") {
      return this.curveIntersect(segment);
    } else if (segment.shapeType === "Spiral") {
      return this.spiralIntersection(segment);
    } else {
      console.error('Unknown Shape type in XML file');
      return false;
    }
  }

  add(vertex) {
    let finished = false;
    let addedPoint = false;
    let valid = false;

    const coordinate = new SceneCoordinate(vertex).toGeocentric();
    const clickPointInData = new SceneCoordinate(vertex).toDataProjection();

    const alignmentInfo = this.alignmentInfo;
    const enu = alignmentInfo.enu;
    const xmlAlignment = alignmentInfo.alignment;

    let units;
    if (alignmentInfo.units === "meter") {
      units = "m";
    } else if (alignmentInfo.units === "USSurveyFoot") {
      units = "sft";
    } else {
      units = "ft";
    }
    this.units = units;

    if (!xmlAlignment) {
      toast.warning(t("toast.station-no-alignment"));
      return {
        finished: finished,
        added: addedPoint,
        valid: valid
      };
    }

    this.enu = enu;

    if (this.enu) {
      this.clickedPoint = [clickPointInData.x, clickPointInData.y, clickPointInData.z];
    } else {
      this.clickedPoint = [clickPointInData.y, clickPointInData.x, clickPointInData.z];
    }

    const segments = xmlAlignment.geometry.filter(segment => this.isBetween(segment));
    const segment = this.getClosestSegment(segments);

    if (segment) {
      finished = this.getIntersect(segment);
      if (finished) {
        this.vertices.push(coordinate);
        addedPoint = true;
        const allCoords = this.vertices;
        valid = (allCoords.length === 2);
      }
    } else {
      toast.warning(t("toast.station-no-segment"));
    }

    return {
      finished: finished,
      added: addedPoint,
      valid: valid
    };
  }

  loadExistingStation(measurement) {
    this.distAlong = measurement.along;
    this.distAcross = Math.abs(measurement.across);
    this.stationStart = measurement.station;
    this.sideOfLine = (measurement.across > 0) ? 'R' : 'L';
    this.units = measurement.units;
  }
}

export class MeasurementLine extends Measurement3D {
  public maxVertexLength = 16;

  constructor() {
    super();
  }

  get moreTooltip() {
    return [
      t('measure.click-to-add-more-points', {
        count: this.numVertices
      }),
      t('measure.double-click-to-finish'),
      this.cancelMessage
    ];
  }

  get moreTooltipMulti() {
    return [
      t('measure.click-to-add-observation-1-multiple', {
        count: this.numVertices
      }),
      this.cancelMessage
    ];
  }

  get endTooltip() {
    return this.moreTooltip;
  }

  get endTooltipMulti() {
    return [
      t('measure.click-to-add-observation-1'),
      t('measure.number-of-points', {
        count: this.numVertices
      }),
      this.cancelMessage
    ];
  }
}

export class MeasurementPoint extends Measurement3D {
  public type = MeasureType.Point;
  public maxVertexLength = 1;
  public minVertexRequired = 1;

  constructor() {
    super();

    this.setTitle("measure.3d-point");
    this.setHeader("measure.3d-point");
  }

  get startTooltip() {
    return [
      t('measure.click-for-single-point')
    ];
  }
}

export class MeasurementArea extends Measurement3D {
  public surfaceGenerator;
  public type = MeasureType.Area;
  public maxVertexLength = 16;
  public minVertexRequired = 3;

  constructor() {
    super();

    this.setTitle("measure.3d-area");
    this.setHeader("measure.3d-area");
  }

  get areaInvalidMessage() {
    return t('measure.invalid-area');
  }

  get startTooltip() {
    return [
      t('measure.click-to-start-area')
    ];
  }

  get moreTooltip() {
    return [
      t('measure.click-to-add-more-points', {
        count: this.numVertices
      }),
      this.cancelMessage
    ];
  }

  get moreTooltipMulti() {
    return [
      t('measure.click-to-add-observation-1-multiple', {
        count: this.numVertices
      }),
      t('measure.number-of-points', {
        count: this.numVertices
      }),
      this.cancelMessage
    ];
  }

  get endTooltip() {
    return [
      t('measure.click-to-add-more-points', {
        count: this.numVertices
      }),
      t('measure.double-click-to-finish'),
      this.cancelMessage
    ];
  }

  get endTooltipMulti() {
    return [
      t('measure.click-to-add-observation-1'),
      t('measure.number-of-points', {
        count: this.numVertices
      }),
      this.cancelMessage
    ];
  }

  get surfaceVertices() {
    return this.surfaceGenerator.vertices;
  }

  get surfaceIndices() {
    return this.surfaceGenerator.indices;
  }

  get area3D() {
    return this.surfaceGenerator.area;
  }

  get valid() {
    return this.surfaceGenerator.valid;
  }

  setController(controller) {
    this.controller = controller;
    this.surfaceGenerator = new SurfaceGenerator(this);
  }

  generateSurface(temporaryPoint: SceneCoordinate = null) {
    this.surfaceGenerator.clear();
    this.surfaceGenerator.generate(temporaryPoint);
  }

  clear() {
    this.surfaceGenerator.clear();
  }
}

export class MeasurementVolume extends MeasurementArea {
  public volumeHelper: VolumeHelper;
  public type = MeasureType.Volume;

  constructor() {
    super();

    this.setHeader("measure.3d-volume");
    this.setTitle("measure.3d-volume");
  }

  get invalidVolumeMessage() {
    return t('measure.invalid-volume');
  }

  get startTooltip() {
    return [
      t('measure.click-to-start-volume')
    ];
  }

  get calculated() {
    return this.volumeHelper.calculated;
  }

  get calculating() {
    return this.volumeHelper.calculatingVolume;
  }

  get volumeType() {
    return this.controller.volumeType;
  }

  get sampleRate() {
    return this.controller.sampleRate;
  }

  get volume() {
    return this.volumeHelper.value;
  }

  async calculateVolume() {
    this.volumeHelper.clear();
    this.volumeHelper.setSampleRate(this.sampleRate);
    this.volumeHelper.setVolumeType(this.volumeType);

    await this.volumeHelper.calculate();

    this.controller.renderMeasurement(this);
    this.controller.refreshMeasurementList();
  }

  /** Cancel webworker and pass empty array as results */
  stopVolumeCalculation() {
    this.volumeHelper.stopCalculation();
  }

  setController(controller) {
    this.controller = controller;
    this.volumeHelper = new VolumeHelper(this, this.pointclouds);
    this.surfaceGenerator = new SurfaceGenerator(this);
  }

  loadExistingVolume(value) {
    if (!value) return;
    this.volumeHelper.setVolume(value);
  }

  clear() {
    this.volumeHelper.destroyWorker();
    this.volumeHelper.clear();
    this.surfaceGenerator.clear();
  }
}

export class HeightMeasurement extends Measurement3D {
  public type = MeasureType.Height

  constructor() {
    super();

    this.setTitle("measure.height-measurement");
    this.setHeader("measure.height-measurement");
  }

  get moreTooltip() {
    return [
      t('measure.select_another_point_to_finish'),
      this.cancelMessage
    ];
  }

  get height() {
    let vertex0 = this.vertices[0].toScene();
    let vertex1 = this.vertices[1].toScene();
    return Math.abs(vertex0.z - vertex1.z);
  }
}

export class SnapMeasurement extends Measurement3D {
  constructor() {
    super();
  }

  add(vertex) {
    let valid = false;
    let added = false;
    let snapResult = this.snap(vertex);

    let coordinate = new SceneCoordinate(vertex).toGeocentric();
    this.vertices.push(coordinate);

    if (snapResult) {
      valid = true;
      added = true;

      let snapCoordinate = new SceneCoordinate(snapResult).toGeocentric();
      this.vertices.push(snapCoordinate);
    }

    return {
      finished: true,
      added: added,
      valid: valid
    };
  }

  snap(vertex) {
    let verticalTolerance = 0.30;
    let finalHeight;
    let validHeights;

    let pos = new Vector3().copy(vertex);
    let points = this.pointclouds.pointsInRadius(pos, 0.15);
    let heights = points.map(point => point.z);

    if (this instanceof SnapDownMeasurement) {
      validHeights = heights.filter(height => vertex.z - height > verticalTolerance);
    } else {
      validHeights = heights.filter(height => height - vertex.z > verticalTolerance);
    }

    if (validHeights.length === 0) {
      return undefined;
    }

    if (this instanceof SnapDownMeasurement) {
      finalHeight = validHeights.reduce((acc, curr) => Math.max(acc, curr));
    } else {
      finalHeight = validHeights.reduce((acc, curr) => Math.min(acc, curr));
    }

    return new Vector3(vertex.x, vertex.y, finalHeight);
  }
}

export class SnapUpMeasurement extends SnapMeasurement {
  constructor() {
    super();
  }

  get startTooltip() {
    return [
      t('measure.click-to-snap-up')
    ];
  }
}

export class SnapDownMeasurement extends SnapMeasurement {
  constructor() {
    super();
  }

  get startTooltip() {
    return [
      t('measure.click-to-snap-down')
    ];
  }
}

export class AerialMeasurement extends Measurement {
  public geometry;

  constructor(geometry = null) {
    super();

    this.setTitle(null);
    this.setHeader("measure.2d-measurement");
    this.geometry = geometry;
  }

  get coordinates() {
    return this.geometry.getCoordinates();
  }

  get numVertices() {
    return this.coordinates[0].length;
  }

  getGeometry() {
    return this.geometry;
  }

  getProjectedVertices(projection) {
    return geometryToArray(this.geometry, projection);
  }

  loadFromCoordinates(coordinates) {}
}

export class AerialLengthMeasurement extends AerialMeasurement {
  public type = MeasureType.Length;

  constructor(geometry = null) {
    super(geometry);

    this.setTitle("measure.2d-length");
    this.setHeader("measure.length");
  }

  get distance2D() {
    return getLength(this.geometry);
  }

  loadFromCoordinates(coordinates) {
    this.geometry = new LineString(coordinates);
  }
}

export class AerialAreaMeasurement extends AerialMeasurement {
  public type = MeasureType.Area

  constructor(geometry = null) {
    super(geometry);

    this.setTitle("measure.2d-area");
    this.setHeader("measure.area");
  }

  get numVertices() {
    return this.coordinates[0].length - 1;
  }

  get area2d() {
    return getArea(this.geometry);
  }

  loadFromCoordinates(coordinates) {
    this.geometry = new Polygon(coordinates);
  }
}

class VolumeHelper {
  private measurement: MeasurementVolume;
  private pointclouds: PointCloudManager;
  public calculatingVolume = false;
  public calculated = false;
  public value =  0.0;
  private sigma = 2.0;
  private numNeighbours = 10;
  private sampleRate = SamplingRate.Medium;
  private volumeType = VolumeType.MultiPlane;
  private worker = null;
  private vertices;
  private bounds;
  private meshes = [];

  constructor(measurement, pointclouds) {
    this.measurement = measurement;
    this.pointclouds = pointclouds;
    this.initWebworker();
    this.clear();
  }

  get scene() {
    return this.measurement.controller.scene;
  }

  setVolumeType(type: VolumeType) {
    this.volumeType = type;
  }

  setSampleRate(rate: SamplingRate) {
    this.sampleRate = rate;
  }

  async calculate() {
    if (this.calculatingVolume) return;

    this.vertices = this.measurement.vertices.map(vertex => {
      return vertex.toScene();
    });

    if (this.volumeType === VolumeType.LowestPlane) {
      const minHeight = Math.min(...this.vertices.map(x => x.z));
      this.vertices.forEach(vertex => {
        vertex.z = minHeight;
      });
    }

    this.bounds = this.getCalculatedBounds();

    await this.calculateVolume();
  }

  initWebworker() {
    const workerURL = "workers/sparse-outliers.js";
    this.worker = new Worker(`${process.env.PUBLIC_URL}/${workerURL}`);
  }

  getCalculatedBounds() {
    let arr = this.vertices;

    let x = arr.map(vertex => vertex.x);
    let xMin = Math.min(...x);
    let xMax = Math.max(...x);

    let y = arr.map(vertex => vertex.y);
    let yMin = Math.min(...y);
    let yMax = Math.max(...y);

    let z = arr.map(vertex => vertex.z);
    let zMin = Math.min(...z);
    let zMax = Math.max(...z);

    let center = new Vector3(
      xMin + (xMax - xMin) / 2.0,
      yMin + (yMax - yMin) / 2.0,
      zMin + (zMax - zMin) / 2.0
    );

    let size = Math.max(xMax - xMin, yMax - yMin);

    return {
      center: center,
      size: size
    };
  }

  async removeOutliers(points): Promise<any> {
    return new Promise(resolve => {
      this.worker.onmessage = event => {
        resolve(event.data);
      };

      this.worker.postMessage({
        points: points,
        sigma: this.sigma,
        numNeighbours: this.numNeighbours
      });
    });
  }

  destroyWorker() {
    if (!this.worker) return;

    this.worker.terminate();
    this.initWebworker();
  }

  drawMesh(points, triangles) {
    const material = new MeshBasicMaterial({
      color: 0xFFFFFF, side: DoubleSide});

    const indices = triangles.flat();
    const geometry = new BufferGeometry().setFromPoints(points);
    geometry.setIndex(indices);

    const mesh = new Mesh(geometry, material);

    this.meshes.push(mesh);
    this.scene.add(mesh);
  }

  setVolume(value) {
    this.calculated = true;
    this.value = value;
  }

  getVolume(points, triangles) {
    let volume = 0;
    triangles.forEach(tri => {
      let p1 = points[tri[0]].clone();
      let p2 = points[tri[1]].clone();
      let p3 = points[tri[2]].clone();
      let signedTetrahedronVolume = p1.dot(p2.cross(p3)) / 6.0;
      volume += signedTetrahedronVolume;
    });

    return volume;
  }

  generateTriangleArray(buffer, reverse = false) {
    let arr = Array.from(buffer);
    let v1 = arr.filter((v, index) => index % 3 === 0);
    let v2 = arr.filter((v, index) => index % 3 === 1);
    let v3 = arr.filter((v, index) => index % 3 === 2);
    let final = [];
    for (var i = 0; i < arr.length / 3; i++) {
      let vertices = [v1[i], v2[i], v3[i]];
      if (reverse) {
        vertices = [v1[i], v3[i], v2[i]];
      }
      final.push(vertices);
    }

    return final;
  }

  removeInvalidTriangles(points, triangles) {
    let validTriangles = [];
    triangles.forEach(triangle => {
      let p1 = points[triangle[0]];
      let p2 = points[triangle[1]];
      let p3 = points[triangle[2]];

      let tri = new Triangle(p1, p2, p3);
      let center = new Vector3();
      tri.getMidpoint(center);
      let valid = pointInPolygon(center, this.vertices);

      if (valid) {
        validTriangles.push(triangle);
      }
    });

    return validTriangles;
  }

  getDelaunayTriangles(points, reverseWinding) {
    let xFunc = point => {
      return point.x;
    };

    let yFunc = point => {
      return point.y;
    };

    let delaunay = Delaunator.from(points, xFunc, yFunc);

    let triangles = this.generateTriangleArray(delaunay.triangles, reverseWinding);
    triangles = this.removeInvalidTriangles(points, triangles);

    return triangles;
  }

  async calculateVolume() {
    this.calculatingVolume = true;
    this.measurement.controller.refreshMeasurementList();

    // Create a more dense version of our boundary polygon
    let denseBounds = getDensePolygon(this.vertices, this.sampleRate);

    // Query points for this bounding box
    let points = this.pointclouds.pointsInRadius(this.bounds.center,
      this.bounds.size / 2.0);

    // Get the points inside a polygon only
    points = this.pointclouds.pointsInPolygon(points, this.vertices);

    const callback = point => {
      let x = Math.floor(point.x / this.sampleRate);
      let y = Math.floor(point.y / this.sampleRate);
      return `${x}-${y}`;
    };

    // Calculate DTM for points, in a 2d grid
    points = this.pointclouds.getSampledPoints(points, true, callback);

    // Remove sparse outliers
    points = await this.removeOutliers(points);

    this.calculationFinished(points, denseBounds);
  }

  calculationFinished(points, denseBounds=[]) {
    this.destroyWorker();

    if (points.length !== 0) {
      this.webWorkerFinished(points, denseBounds);
    }

    this.calculatingVolume = false;
    this.measurement.controller.refreshMeasurementList();
  }

  /** @private */
  webWorkerFinished(pointsArray, denseBounds) {
    const points = pointsArray.map(point => {
      return new Vector3().copy(point);
    });

    // Calculate upper mesh with reverse winding order
    const upperPoints = [...denseBounds, ...points];
    const trianglesUpper = this.getDelaunayTriangles(
      upperPoints, true);

    // Calculate lower mesh
    const lowerPoints = [...denseBounds];
    const trianglesLower = this.getDelaunayTriangles(
      lowerPoints, false);

    const trianglesCombined = [
      ...trianglesLower,
      ...trianglesUpper
    ];

    this.value = this.getVolume(upperPoints, trianglesCombined);
    this.drawMesh(upperPoints, trianglesCombined);
    this.calculated = true;
  }

  /** @private */
  stopCalculation() {
    if (!this.calculatingVolume) return;
    this.calculationFinished([]);
  }

  clear() {
    this.meshes.forEach(mesh => {
      this.scene.remove(mesh);
    });

    this.meshes = [];
    this.value = null;
    this.calculatingVolume = false;
    this.calculated = false;
  }
}

class SurfaceGenerator {
  private measurement: MeasurementArea;
  private changeRequired = 0.01;
  private renderInScene = false;
  private indices = [];
  private meshes = [];
  public vertices = [];
  public area = 0;

  constructor(measurement) {
    this.measurement = measurement;
    this.clear();
  }

  get scene() {
    let measurement = this.measurement;
    return measurement.controller.scene;
  }

  get valid() {
    return this.indices.length > 0;
  }

  clear() {
    this.removeMeshesFromScene();
    this.indices = [];
    this.meshes = [];
  }

  addMeshesToScene() {
    if (!this.meshes || !this.renderInScene) {
      return;
    }

    this.meshes.forEach(mesh => {
      this.scene.add(mesh);
    });
  }

  removeMeshesFromScene() {
    if (!this.meshes || !this.renderInScene) {
      return;
    }

    this.meshes.forEach(mesh => {
      this.scene.remove(mesh);
    });
  }

  generateMesh(triangle) {
    let hexColor = randomHexColor(); // TODO: remove random coloring later

    const vertices = [triangle.a, triangle.b, triangle.c];
    const geometry = new BufferGeometry()
      .setFromPoints(vertices);

    const material = new MeshBasicMaterial({
      color: hexColor,
      side: DoubleSide
    });

    const mesh = new Mesh(geometry, material);

    return mesh;
  }

  calculateArea() {
    let area = 0;

    this.indices.forEach(indices => {
      let triangle = new Triangle(
        this.vertices[indices[0]],
        this.vertices[indices[1]],
        this.vertices[indices[2]]
      );
      area += triangle.getArea();
    });

    this.area = area;
  }

  optimize(vertices, triangleIndices) {
    let verticesModified = false;
    let numIndices = triangleIndices.length;

    for (var triIndexA = 0; triIndexA < numIndices; triIndexA++) {
      for (var triIndexB = 0; triIndexB < numIndices; triIndexB++) {
        if (verticesModified) {
          continue;
        }

        let triA = triangleIndices[triIndexA];
        let triB = triangleIndices[triIndexB];

        let vertSet = new Set([...triA, ...triB]);
        let allVerts = Array.from(vertSet);
        if (allVerts.length !== 4) {
          continue;
        }

        let sharedVerts = triA.filter(x => triB.includes(x));
        let uniqueVerts = allVerts.filter(x => !sharedVerts.includes(x));

        let modTriA = [...uniqueVerts, sharedVerts[0]];
        let modTriB = [...uniqueVerts, sharedVerts[1]];

        let t1 = new Triangle(vertices[triA[0]],
          vertices[triA[1]], vertices[triA[2]]);
        let t2 = new Triangle(vertices[triB[0]],
          vertices[triB[1]], vertices[triB[2]]);
        let oldArea = t1.getArea() + t2.getArea();

        let t3 = new Triangle(vertices[modTriA[0]],
          vertices[modTriA[1]], vertices[modTriA[2]]);
        let t4 = new Triangle(vertices[modTriB[0]],
          vertices[modTriB[1]], vertices[modTriB[2]]);
        let newArea = t3.getArea() + t4.getArea();

        // New area must be smaller
        if (newArea > oldArea) {
          continue;
        }

        // Difference between areas must be non-trivial
        let diff = (oldArea - newArea) / oldArea;
        if (diff < this.changeRequired) {
          continue;
        }

        triangleIndices[triIndexA] = modTriA;
        triangleIndices[triIndexB] = modTriB;
        verticesModified = true;
      }
    }

    if (verticesModified) {
      return this.optimize(vertices, triangleIndices);
    }

    return triangleIndices;
  }

  generate(temporaryPoint: SceneCoordinate) {
    this.vertices = this.measurement.vertices.map(vertex => {
      return vertex.toScene();
    });

    if (temporaryPoint) {
      this.vertices.push(temporaryPoint);
    }

    let numVertices = this.vertices.length;
    let triangles = [];

    if (numVertices === 3) {
      triangles.push([0, 1, 2]);
    } else if (numVertices > 3) {
      let points = calculatePointsPCA(this.vertices, true);
      let indices = earcut(points, null, 3);
      let deviation = earcut.deviation(points, null, 3, indices);

      // Deviation must be nearly zero to be valid
      if (deviation > 1E-6) {
        return;
      }

      let triangleIndices = [];
      let numTriangles = indices.length / 3;
      for (var i = 0; i < numTriangles; i++) {
        triangleIndices.push([
          indices[3 * i],
          indices[3 * i + 1],
          indices[3 * i + 2]
        ]);
      }

      // Optimized surface meshes
      triangleIndices = this.optimize(this.vertices, triangleIndices);

      triangleIndices.forEach(triVertices => {
        triangles.push(triVertices);
      });
    }

    let meshes = [];
    triangles.forEach(indices => {
      let triangle = new Triangle(
        this.vertices[indices[0]],
        this.vertices[indices[1]],
        this.vertices[indices[2]]
      );
      let mesh = this.generateMesh(triangle);
      meshes.push(mesh);
    });

    this.indices = triangles;
    this.meshes = meshes;

    this.addMeshesToScene();
    this.calculateArea();
  }
}

const getDensePolygon = (vertices, spacing) => {
  let densePolygonTemp = [];

  vertices.forEach((start, index) => {
    let nextIndex = (index + 1) % vertices.length;

    let end = vertices[nextIndex];
    let direction = new Vector3().add(end).sub(start);
    let vectorLength = direction.length();
    let steps = Math.floor(vectorLength / spacing);
    let increment = vectorLength / steps;
    direction.setLength(1.0);

    if (steps === 0) {
      densePolygonTemp.push(start);
      return;
    }

    for (var i = 0; i <= steps; i++) {
      let directionIncrement = new Vector3().add(direction)
        .multiplyScalar(increment * i);
      let final = new Vector3().add(start).add(directionIncrement);
      densePolygonTemp.push(final);
    }
  });

  let densePolygon = [];
  densePolygonTemp.forEach((point, index) => {
    if (index === 0) {
      densePolygon.push(point);
    } else {
      let old = densePolygonTemp[index - 1];
      let distance = point.distanceTo(old);
      if (distance > 0) {
        densePolygon.push(point);
      }
    }
  });

  return densePolygonTemp;
};
